import React, { FC, useEffect, useState } from 'react'
import t from 'src/translations/main'
import utils, { checkMode } from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import updateModal from 'src/components/Sections/Modal/updateModal'
import {
  useContactsDetails,
  useContactsDetailsUpdate,
} from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Table from 'src/components/Organisms/Table/Table'
import Font from 'src/components/Core/Typography/Font/Font'
import ModalContactDetailsForm from './ModalContactDetails/ModalContactDetailsForm'

import {
  FormStyle,
  FormHeader,
  FormTitle,
  FormFooter,
  FormFooterActionsLeft,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import {
  ApiData,
  FormProps,
  TableConfigParam,
  TableObj,
} from '../../../../Pages/Onboarding/types'
import useForm, { OnSubmitParams } from 'src/components/Core/Hooks/useForm/useForm'
import config from './config'
import storage from 'src/components/Core/Utils/storage'
import { getEndpointUrl } from 'src/components/Core/Utils/url_constants'

// TODO change the following to actual URLs when available
const UPDATE_CONTACT_URL = 'CorpAOF/updatewebsiteaccesscontactdetails16'
const DELETE_CONTACT_URL = 'CorpAOF/deletewebsiteaccesscontact16'
const ADD_CONTACT_URL = 'CorpAOF/addwebSiteaccesscontacts16'

const tableConfig: TableConfigParam[] = [
  { key: 'fullName' },
  { key: 'email' },
  {
    name: 'pencil',
    color: 'ultraViolet',
  },
  {
    name: 'close',
    color: 'error',
  },
]

const tableColumns = [
  {
    Header: t(
      'onboarding.forms.CorporateAccount.WebsiteAccessForm.table.col1'
    ),
    accessor: 'col1',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.WebsiteAccessForm.table.col2'
    ),
    accessor: 'col2',
  },
  {
    Header: '',
    accessor: 'col3',
  },
  {
    Header: '',
    accessor: 'col4',
  },
]

const WebsiteAccessForm: FC<FormProps> = props => {
  const [initialRender, setInitialRender] = useState(true)
  const tableData = useContactsDetails()
  const updateTableData = useContactsDetailsUpdate()

  const submitForm = (data: OnSubmitParams) => {
    const { submit } = props
    if (submit) submit(data)
  }

  const {
    formData,
    emptyValidation,
    handleInputChange,
    handleSubmit,
    configData,
  } = useForm(props.fetchedData, config, submitForm)

  const [showContactsTable, setShowContactsTable] = useState<boolean>()

  useEffect(() => {
    setShowContactsTable(formData?.accessToAccountActivity?.value === 'true')
  }, [formData?.accessToAccountActivity?.value])

  const { sendRequest } = useFetch()

  const applicationId = storage?.get(storage?.keys?.onboarding?.applicationId)

  const editContactDetails = (id: number) => {
    updateModal({
      title: t('onboarding.modals.ContactsDetails.title'),
      modalState: 'open',
      content: (
        <ModalContactDetailsForm
          actionType='edit'
          action={action}
          tableConfig={tableConfig}
          postUrl={getEndpointUrl(UPDATE_CONTACT_URL)}
          id={id}
        />
      ),
      removeFooter: true,
    })
  }

  const deleteContact = (id: number) => {
    const modifyResponse = (data: ApiData) => {
      if (data) {
        updateTableData((prevState: TableObj[]) => {
          return utils.deleteTableRow([...prevState], id)
        })
        updateModal({ modalState: 'close' })
      }
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl(DELETE_CONTACT_URL, applicationId, 'postMessage.json', `WebsiteAccessContactId=${id}`),
        method: 'DELETE',
      },
      modifyResponse
    )
  }

  const openConfirmDeleteModal = (id: number) => {
    updateModal({
      title: t('onboarding.modals.ContactsDetails.delete.title'),
      modalState: 'open',
      content: (
        <Font>
          {t('onboarding.modals.ContactsDetails.delete.content')}
        </Font>
      ),
      footerActions: [
        {
          label: t('components.forms.labels.yes'),
          version: 2,
          onClick: () => {
            deleteContact(id)
          },
        },
        {
          label: t('components.forms.labels.no'),
          onClick: () => {
            updateModal({ modalState: 'close' })
          },
        },
      ],
    })
  }

  const action = (id: number, action: string) => {
    if (action === 'edit') editContactDetails(id)
    if (action === 'delete') openConfirmDeleteModal(id)
  }

  const createTableData = (
    apiData: ApiData,
    tableConfig: TableConfigParam[]
  ) => {
    const newTableData: TableObj[] = []
    apiData.forEach((rowDataObj: ApiData) => {
      const tableObj = utils.createTableObj(
        rowDataObj,
        tableConfig,
        action
      )
      newTableData.push(tableObj)
    })
    if(initialRender === true) setInitialRender(false)
    return newTableData
  }

  useEffect(() => {
    if(formData?.webSiteAccessContacts && formData.webSiteAccessContacts.value) {
      updateTableData(
        // @ts-ignore
        createTableData(formData.webSiteAccessContacts.value, tableConfig)
      )
    }
  }, [formData?.webSiteAccessContacts])

  useEffect(() => {
    if(!initialRender && props.fetchedData.accessToAccountActivity !== 'false' && tableData?.length === 0){
      if(props.setOptionsList){
        props.setOptionsList(prevList => (
          prevList?.map((obj, index) => {
            if (index === 16) {
              obj.state = 'in_progress'
            }
            return obj
          })
        ))
      }

      if(props.setCompletedStepList){
        props.setCompletedStepList(prevList => (
          prevList?.map((value, index) => {
            return index === 16 ? false : value
          })
        ))
      }
    }

  }, [tableData])

  return (
    <FormStyle onSubmit={handleSubmit}>
      <FormHeader>
        <FormTitle>
          {t('onboarding.forms.CorporateAccount.WebsiteAccessForm.title')}
        </FormTitle>
      </FormHeader>
      {utils.sectionBuilder(configData, formData, handleInputChange)}
      {showContactsTable && (
        <Button
          version={2}
          label={t('onboarding.modals.ContactsDetails.openButton')}
          onClick={() =>
            updateModal({
              title: t('onboarding.modals.ContactsDetails.title'),
              modalState: 'open',
              content: (
                <ModalContactDetailsForm
                  actionType='add'
                  action={action}
                  tableConfig={tableConfig}
                  postUrl={getEndpointUrl(ADD_CONTACT_URL)}
                />
              ),
              removeFooter: true,
            })
          }
          disabled={checkMode('readOnly')}
        />
      )}
      {showContactsTable && tableData && tableData.length > 0 && (
        <Table
          tableData={tableData}
          tableColumns={tableColumns}
          tableReadOnly={checkMode('readOnly')}
        />
      )}
      <FormFooter noOfButtons={2}>
        <FormFooterActionsLeft>
          <Button
            label={t('onboarding.main.backButton')}
            version={2}
            disabled={props.disablePrev}
            onClick={props.prevStep}
          />
        </FormFooterActionsLeft>
        <FormFooterActionsRight>
          <Button
            type='submit'
            label={props.isLastForm ? t('onboarding.main.saveAndSubmit') : t('onboarding.main.continueButton')}
            disabled={emptyValidation || (formData.accessToAccountActivity.value === 'true' && tableData?.length === 0)}
          />
        </FormFooterActionsRight>
      </FormFooter>
    </FormStyle>
  )
}

export default WebsiteAccessForm
